import { Rectangle } from "../../Utils/Shapes/Rectangle";
import { Game, eAtlas_0, eModelAsset, eSfxSprite, eAudioAsset, SfxSprites } from "../../Game";
import { Pool } from "../../Utils/Collections/Pool";
import { TIMER, Gameplay, eBatchGroup } from "../Gameplay";
import { ModelUtils } from "../../Utils/ModelUtils";
import { Character, eDeathReason } from "../Player/Character";
import { TextureAtlasUtils } from "../../Utils/TextuAtlasUtils";



export class ObstacleTypeShadow {
    get frameKey(): string { return this._frameKey; }
    private _frameKey: string;

    get size(): pc.Vec2 { return this._size; }
    private _size: pc.Vec2;

    get offset(): pc.Vec3[] { return this._offset; }
    private _offset: pc.Vec3[];

    constructor(frameKey: string, size: pc.Vec2, offset: pc.Vec3[]) {
        this._frameKey = frameKey;
        this._size = size;
        this._offset = offset;
    }
}



export class ObstacleType {
    /**
     * (RO) Index typu
     */
    get uid(): number { return this._uid; }
    protected _uid: number;

    /**
     * (RO) Kolizni obdelnik
     */
    get colRect(): Rectangle { return this._colRect; }
    protected _colRect: Rectangle;

    /**
     * (RO) Celkova velikost prekazky na ose X
     */
    get width(): number { return this._width; }
    protected _width: number;

    /**
     * (RO) Pozice prekazky na ose Y
     */
    get y(): number { return this._y; }
    protected _y: number;

    /**
     * Jmena assetu s modelem
     */
    protected _modelAssetKey: string;

    /**
     * Prvni entita, ktea se dal klonuje
     */
    protected _1stEntity: pc.Entity;

    /**
     * (RO) Material pro mesh instanci s danym indexem
     */
    get meshMat1(): pc.Material[] { return this._meshMat1; }
    protected _meshMat1: pc.StandardMaterial[];

    /**
     * (RO) Material pro mesh instanci s danym indexem. U tohoto materialu lze menit opacity
     */
    get meshMat2(): pc.Material[] { return this._meshMat2; }
    protected _meshMat2: pc.StandardMaterial[];

    get shadow(): ObstacleTypeShadow { return this._shadow; }
    protected _shadow: ObstacleTypeShadow;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(uid: number, modelAssetKey: string, y: number, width: number, colRcSize: pc.Vec2, colRcCentre: pc.Vec2, shadow?: ObstacleTypeShadow) {
        this._uid = uid;
        this._modelAssetKey = modelAssetKey;
        this._y = y;
        this._width = width;
        this._colRect = new Rectangle(colRcCentre.x - colRcSize.x / 2, colRcCentre.y - colRcSize.y / 2, colRcSize.x, colRcSize.y);
        this._shadow = shadow;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    createEntity(): pc.Entity {
        let entity: pc.Entity;

        if (!this._1stEntity) {
            entity = new pc.Entity();

            entity.addComponent(pc.eCompomentType.model, {
                type: pc.eModelType.asset,
                asset: Game.APP.assets.find(this._modelAssetKey, pc.eAssetType.model),
                castShadows: false,
                castShadowsLightmap: false,
                receiveShadows: false,
                batchGroupId: Gameplay.instance.batchGroups[eBatchGroup.dynamic]
            });

            // Pripravit nove materialy na vykreslovani s opacity < 1
            this._meshMat1 = [];
            this._meshMat2 = [];

            ModelUtils.cloneModelMaterials(entity.model, {
                blendType: pc.BLEND_NORMAL,
                opacity: 0.1
            }, this._meshMat1, this._meshMat2, Game.APP.assets, "opacity_");

            this._1stEntity = entity;

            // Stin
            if (this._shadow) {
                let atlas = <pc.TextureAtlas>Game.APP.assets.find(eAtlas_0.NAME, pc.eAssetType.atlas).resource;
                let shadow = new pc.Entity("shadow");

                shadow.addComponent(pc.eCompomentType.element, {
                    type: pc.ELEMENTTYPE_IMAGE,
                    texture: atlas.texture,
                    width: this._shadow.size.x,
                    height: this._shadow.size.y,
                    opacity: Game.FAKE_SHADOW_OPACITY,
                    anchor: [0.5, 0.5, 0.5, 0.5],
                    pivot: [0.5, 0.5],
                    batchGroupId: Gameplay.instance.batchGroups[eBatchGroup.fakeShadows]
                });
                shadow.setLocalEulerAngles(-90, 0, 0);
                shadow.setLocalPosition(this._shadow.offset[0]);
                shadow.element.rect = TextureAtlasUtils.getTexFrameRegion(atlas.texture, (<pc.TextureAtlasFrame>atlas.frames[this._shadow.frameKey]).rect);
                entity.addChild(shadow);
            }
        }
        else {
            entity = this._1stEntity.clone();
        }

        entity.setLocalPosition(0, this._y, 0);
        entity.enabled = false;

        return entity;
    }
}



export const enum eBasicObstacleFlag {
    active = 1,
    destruction = 2,
    checkCollision = 4,
}



/**
 * Zakladni verze prekazky
 */
export class BasicObstacle {
    /**
     * Flagy
     */
    protected _flags: number;

    /** 
     * (RO) Typ prekazky
     */
    get type(): ObstacleType { return this._type; }
    protected _type: ObstacleType;

    /**
     * (RO) Kolizni obdelnik prekazky aktualizovany v update()
     */
    get colRect(): Rectangle { return this._colRect; }
    protected _colRect: Rectangle;

    /**
     * (RO) True, pokud se ma testovat kolize prekazky s hracem
     */
    get checkCollision(): boolean { return (this._flags & eBasicObstacleFlag.checkCollision) != 0; }

    /**
     * Pool s volnymi prekazkami tohoto typu
     */
    protected _freeObstacles: Pool<BasicObstacle>;

    /**
     * (RO) Entita
     */
    get entity(): pc.Entity { return this._entity; }
    protected _entity: pc.Entity;

    /**
     * Stin
     */
    protected _shadow: pc.Entity;

    /**
     * Cas zahajeni zniceni
     */
    protected _destrTime: number;

    /**
     * Uhel prekazky na ose Y pri zahajeni jejiho zniceni
     */
    protected _destrAngles: pc.Vec3;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(type: ObstacleType, freeObstacles: Pool<BasicObstacle>) {
        this._type = type;
        this._colRect = new Rectangle();
        this._freeObstacles = freeObstacles;
        this._entity = type.createEntity();
        this._shadow = this._entity.findByName("shadow") as pc.Entity;
        this._destrAngles = new pc.Vec3();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): BasicObstacle {
        if ((this._flags & eBasicObstacleFlag.active) != 0) {
            this._flags &= ~eBasicObstacleFlag.active;

            // Zakazat entitu            
            this._entity.enabled = false;

            // Vratit prekazku do poolu s volnymi prekazkami
            this._freeObstacles.returnItem(this);
        }

        return this;
    }

    //-------------------------------------------------------------------------
    /**
     * Aktivace prekazky
     * @param parent 
     * @param z
     * @param xDir -1 = smer vlevo, 1 = smer vpravo
     * @param x 
     * @param data 
     */
    //-------------------------------------------------------------------------
    activate(parent: pc.Entity, z: number, xDir: number, x: number, data?: any): BasicObstacle {
        this._flags = eBasicObstacleFlag.checkCollision | eBasicObstacleFlag.active;

        // Nastavit entitu
        let pos = this._entity.getLocalPosition();

        pos.set(x, this._type.y, z);

        if (this._entity.parent != parent)
            this._entity.reparent(parent);

        this._entity.setLocalPosition(pos);
        this._entity.setLocalScale(1, 1, 1);
        this._entity.enabled = true;

        // Nastavit kolizni obdelnik
        this._colRect.setFrom(this._type.colRect);

        if (xDir > 0)
            this._colRect.x = -(this._colRect.x + this._colRect.width);

        this._colRect.move(pos.x, pos.z);

        // Nastavit vychozi opacity materialum
        let batchGroupId = Gameplay.instance.batchGroups[eBatchGroup.dynamic];

        if (this._entity.model.batchGroupId != batchGroupId) {
            // Vykreslovat entitu s nepruhlednym materialem
            this._entity.model.meshInstances.forEach((mesh: pc.MeshInstance, i: number) => {
                mesh.material = this._type.meshMat1[i];
                mesh.setParameter("material_opacity", 1);
            });

            this._entity.model.batchGroupId = batchGroupId;
            this._entity.model.castShadows = true;
        }

        // Stin
        if (this._shadow)
            this._shadow.element.enabled = true;

        return this;
    }

    //-------------------------------------------------------------------------
    /**
     * Zniceni prekazky pres animaci
     */
    //-------------------------------------------------------------------------
    destroy(): void {
        if ((this._flags & eBasicObstacleFlag.destruction) != 0)
            return;

        this._flags |= eBasicObstacleFlag.destruction;
        this._destrTime = TIMER.time;
        this._destrAngles.copy(this._entity.getLocalEulerAngles());

        this._flags &= ~eBasicObstacleFlag.checkCollision;

        // Odebrat model z batchGroup jelikoz u nej budeme menit opacity materialu
        this._entity.model.batchGroupId = -1;

        this._entity.model.meshInstances.forEach((mesh: pc.MeshInstance, i: number) => {
            mesh.material = this._type.meshMat2[i];
        });

        // Skryt stin
        if (this._shadow)
            this._shadow.element.enabled = false;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): boolean {
        if ((this._flags & eBasicObstacleFlag.destruction) != 0) {
            // Zniceni prekazky
            let progress = (TIMER.time - this._destrTime) / 1000;

            if (progress >= 1) {
                this.reset();
                return false;
            }

            progress = pc.CubicOut(progress);

            // Rotace
            let i = 540 * progress;
            this._entity.setLocalEulerAngles(this._destrAngles.x, this._destrAngles.y + i, this._destrAngles.z);

            i = 1 + 0.5 * progress;
            this._entity.setLocalScale(i, i, i);

            // Opacity
            //ModelUtils.setOpacity(this._entity.model, 1 - progress);
        }

        return true;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    handlePlCollision(plChar: Character): void {
        // Znicit prekazku
        this.destroy();

        if (!plChar.hasShield)
            // Zabit postavicku
            plChar.kill(eDeathReason.collision);
    }
}